import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';

// Spiegelung von src/server/session.ts:sessionRoleSchema. 'reinigung' ist
// noch nicht freigeschaltet (CEO-Variante-B, F-401/F-804) — wir typisieren
// hier nur, was das Backend tatsaechlich vergibt.
type RoleName = 'admin' | 'buero' | 'aussendienst' | 'pending';

interface UserRow {
  id: string;
  email: string;
  displayName: string;
  roleName: RoleName | null;
  entraUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface RoleRow {
  id: number;
  name: RoleName;
  description: string | null;
}

export const Route = createFileRoute('/users')({
  beforeLoad: async () => {
    try {
      await api('/api/v1/users');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({ to: '/login' });
      }
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({
          to: '/',
          search: { error: 'users-forbidden' } as Record<string, string>,
        });
      }
      throw err;
    }
  },
  component: UsersPage,
});

function UsersPage() {
  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => api<{ items: UserRow[] }>('/api/v1/users'),
  });
  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<{ items: RoleRow[] }>('/api/v1/roles'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Benutzer & Rollen</h1>
          <p className="text-sm text-slate-600 mt-1">
            Rollen vergeben, Anzeigenamen korrigieren. Eigene Rolle kann nicht geändert werden.
          </p>
        </div>
      </div>

      {usersQ.isLoading && <div className="text-slate-500">Lade…</div>}
      {usersQ.error instanceof Error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {usersQ.error.message}
        </div>
      )}

      {usersQ.data && rolesQ.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">E-Mail</th>
                <th className="px-4 py-2 text-left">Rolle</th>
                <th className="px-4 py-2 text-left">Entra</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {usersQ.data.items.map((u) => (
                <UserRow key={u.id} user={u} roles={rolesQ.data.items} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UserRow({ user, roles }: { user: UserRow; roles: RoleRow[] }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user.displayName);
  const [role, setRole] = useState<RoleName>(user.roleName ?? 'pending');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: { displayName?: string; role?: RoleName } = {};
      if (displayName !== user.displayName) patch.displayName = displayName;
      if (role !== user.roleName) patch.role = role;
      if (Object.keys(patch).length === 0) return user;
      return api<{ item: UserRow }>(`/api/v1/users/${user.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      setEditing(false);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Fehler';
      setError(msg);
    },
  });

  if (!editing) {
    return (
      <tr className="text-sm text-slate-800">
        <td className="px-4 py-2 font-medium">{user.displayName}</td>
        <td className="px-4 py-2 text-slate-600">{user.email}</td>
        <td className="px-4 py-2">
          <RoleBadge role={user.roleName} />
        </td>
        <td className="px-4 py-2 text-slate-500 text-xs">{user.entraUserId ? 'verknüpft' : '–'}</td>
        <td className="px-4 py-2 text-right">
          <Button
            variant="ghost"
            onClick={() => {
              setDisplayName(user.displayName);
              setRole(user.roleName ?? 'pending');
              setEditing(true);
            }}
          >
            Bearbeiten
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="text-sm bg-brand-50/50">
      <td className="px-4 py-2">
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </td>
      <td className="px-4 py-2 text-slate-600">{user.email}</td>
      <td className="px-4 py-2">
        <Select value={role} onChange={(e) => setRole(e.target.value as RoleName)}>
          {roles.map((r) => (
            <option key={r.id} value={r.name}>
              {r.name}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-4 py-2 text-slate-500 text-xs">{user.entraUserId ? 'verknüpft' : '–'}</td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-2">
          {error && <span className="text-xs text-red-600">{error}</span>}
          <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function RoleBadge({ role }: { role: RoleName | null }) {
  const colors: Record<string, string> = {
    admin: 'bg-amber-100 text-amber-800 border-amber-200',
    buero: 'bg-blue-100 text-blue-800 border-blue-200',
    aussendienst: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    reinigung: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    pending: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  const c = colors[role ?? 'pending'] ?? colors['pending'] ?? '';
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${c}`}
    >
      {role ?? '–'}
    </span>
  );
}
